import React, { useEffect, useRef } from 'react'
import { Deal } from '../models/shared/Deal'
import { Badge, FormControl, Button } from 'react-bootstrap'
import { useText } from '../context/TextContext';
import { formatDate } from '../utils/date';

interface Props {
    deal: Deal,
    focus?: boolean;
    readOnly?: boolean;
    onDealUpdate?: (update: Partial<Deal>) => void
    className?: string;
}

export default function DealDueDate(props: Props) {
    const { deal, onDealUpdate, focus } = props;
    const texts = useText();
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (focus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [focus]);

    // Format date to YYYY-MM-DD for input field
    const formatDateForInput = (date?: Date | null): string => {
        if (!date) return '';

        if (typeof date === 'string') {
            date = new Date(date);
        }

        return date instanceof Date && !isNaN(date.getTime())
            ? date.toISOString().split('T')[0]
            : '';
    };

    // Handle date change
    const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!onDealUpdate) return;
        const newDateStr = event.target.value;

        if (newDateStr) {
            // Convert string date to Date object when saving
            const newDate = new Date(newDateStr);
            onDealUpdate({ dueAt: newDate });
        }
    };

    // Handle clear date
    const handleClearDate = () => {
        if (onDealUpdate) {
            onDealUpdate({ dueAt: null });
        }
    };

    let badgeBg = 'secondary';
    if (deal.dueAt) {
        const dueDate = new Date(deal.dueAt);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (dueDate < today) {
            badgeBg = 'danger';
        } else if (dueDate <= new Date(today.setDate(today.getDate() + 3))) {
            badgeBg = 'warning';
        } else {
            badgeBg = 'success';
        }
    }

    if (props.readOnly) {
        return (
            <Badge pill bg={badgeBg} className={"center gap-2 px-2"}>
                <i className="bi bi-calendar-date" />
                {deal.dueAt && formatDate(deal.dueAt, { noTime: true, noYear: true })}
            </Badge>
        )
    }

    return (
        <Badge pill bg={badgeBg} className={"d-flex align-items-center gap-2 px-3 "}>
            <label className="me-2">{texts.dueDate}:</label>
            <FormControl
                ref={inputRef}
                type="date"
                style={{ width: 'auto' }}
                size="sm"
                value={formatDateForInput(deal.dueAt)}
                onChange={handleDateChange}
            />
            <Button
                variant="link"
                className="p-0 ms-1 text-white"
                style={{ fontSize: '0.8rem' }}
                onClick={handleClearDate}
            >
                <i className="bi bi-x-circle-fill" />
            </Button>
        </Badge>
    )
}
